import YAML from 'yaml'
import type { RenderData, RenderOptions } from '../assetTypes'
import { collectAssetReferencesFromRenderData, normalizeAssetId } from '../shared/assetReferences'
import buildDatasetDataApiScript from '../datasets/dataApiScript'

type RawDashboard = Record<string, unknown>

type DashboardParamValue = string | string[]
type DashboardParamValues = Record<string, DashboardParamValue>

type DashboardItem = {
  width: number | null
  uuid: string
  params: DashboardParamValues
}

type DashboardRow = {
  height: string
  items: DashboardItem[]
}

type DashboardFilterType = 'multi' | 'single' | 'stepper'

type DashboardFilter = {
  reference: string
  uuid: string
  type: DashboardFilterType
  defaultValue: string
}

type DashboardTab = {
  label: string
  params: DashboardParamValues
  filters: DashboardFilter[]
  rows: DashboardRow[]
}

type ParsedDashboard = {
  tabs: DashboardTab[]
  errors: string[]
}

const ASSET_REF_PATTERN = /^asset:\/\/([0-9a-fA-F-]{36})$/
const insightsServiceUrl = 'https://api.shared-platform.eidu.com/test-insights'

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const parseHeight = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${Math.max(0, Math.round(value))}px`
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (/^\d+(\.\d+)?px$/.test(trimmed)) return trimmed
  }
  return null
}

const parseWidth = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (trimmed.endsWith('%')) {
      const numeric = Number(trimmed.slice(0, -1))
      return Number.isFinite(numeric) ? numeric : null
    }
    const numeric = Number(trimmed)
    return Number.isFinite(numeric) ? numeric : null
  }
  return null
}

const normalizeItemParamValue = (value: unknown): DashboardParamValue | null => {
  if (value === null || value === undefined) return null
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined).map((item) => String(item))
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return null
}

const parseParamsObject = (value: unknown, contextLabel: string, errors: string[]): DashboardParamValues | null => {
  if (value === undefined || value === null) return {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    errors.push(`${contextLabel}: params must be an object with scalar or array values.`)
    return null
  }

  const params: DashboardParamValues = {}
  for (const [rawKey, rawValue] of Object.entries(value as RawDashboard)) {
    const key = rawKey.trim()
    if (!key) {
      errors.push(`${contextLabel}: params keys must be non-empty strings.`)
      return null
    }
    const normalized = normalizeItemParamValue(rawValue)
    if (normalized === null) {
      errors.push(`${contextLabel}: params["${rawKey}"] must be a scalar or array.`)
      return null
    }
    params[key] = normalized
  }
  return params
}

const parseRows = (rawRows: unknown[], contextLabel: string, errors: string[]) => {
  const rows: DashboardRow[] = []
  rawRows.forEach((row, rowIndex) => {
    const prefix = `${contextLabel} row ${rowIndex + 1}`
    if (!row || typeof row !== 'object') {
      errors.push(`${prefix}: must be an object.`)
      return
    }
    const rowRecord = row as RawDashboard
    const height = parseHeight(rowRecord.height)
    if (!height) {
      errors.push(`${prefix}: height must be a pixel value (number or "240px").`)
      return
    }
    const rawItems = Array.isArray(rowRecord.items) ? rowRecord.items : []
    if (rawItems.length === 0) {
      errors.push(`${prefix}: items must be a non-empty array.`)
      return
    }

    const items: DashboardItem[] = []
    rawItems.forEach((item, itemIndex) => {
      const itemPrefix = `${prefix} item ${itemIndex + 1}`
      if (!item || typeof item !== 'object') {
        errors.push(`${itemPrefix}: must be an object.`)
        return
      }
      const itemRecord = item as RawDashboard
      const hasWidth = Object.prototype.hasOwnProperty.call(itemRecord, 'width')
      const width = hasWidth ? parseWidth(itemRecord.width) : null
      if (hasWidth && width === null) {
        errors.push(`${itemPrefix}: width must be a percentage number when provided.`)
        return
      }
      const assetRef = typeof itemRecord.asset === 'string' ? itemRecord.asset.trim() : ''
      if (!assetRef) {
        errors.push(`${itemPrefix}: asset is required.`)
        return
      }
      const match = ASSET_REF_PATTERN.exec(assetRef)
      if (!match) {
        errors.push(`${itemPrefix}: asset must be asset://<uuid>.`)
        return
      }
      const params = parseParamsObject(itemRecord.params, itemPrefix, errors)
      if (!params) return

      items.push({ width, uuid: match[1], params })
    })

    if (items.length === 0) {
      errors.push(`${prefix}: has no valid items.`)
      return
    }
    rows.push({ height, items })
  })
  return rows
}

const resolveRowItemWidths = (items: DashboardItem[]) => {
  const explicitTotal = items.reduce((total, item) => total + (item.width ?? 0), 0)
  const autoCount = items.reduce((count, item) => (item.width === null ? count + 1 : count), 0)
  if (autoCount === 0) {
    return items.map((item) => item.width ?? 0)
  }
  const remainingWidth = Math.max(0, 100 - explicitTotal)
  const autoWidth = remainingWidth / autoCount
  return items.map((item) => (item.width === null ? autoWidth : item.width ?? 0))
}

const parseDashboard = (content: string): ParsedDashboard | { error: string; details?: string[] } => {
  let parsed: unknown
  try {
    parsed = YAML.parse(content || '')
  } catch (error) {
    return { error: 'Failed to parse YAML.', details: [String(error)] }
  }

  if (!parsed || typeof parsed !== 'object') {
    return { error: 'Dashboard YAML must be an object with tabs.' }
  }

  const record = parsed as RawDashboard
  const rawTabs = Array.isArray(record.tabs) ? record.tabs : []
  if (rawTabs.length === 0) {
    return { error: 'Dashboard YAML must include a non-empty tabs array.' }
  }

  const errors: string[] = []
  const tabs: DashboardTab[] = []

  rawTabs.forEach((tab, tabIndex) => {
    const tabLabel = `Tab ${tabIndex + 1}`
    if (!tab || typeof tab !== 'object') {
      errors.push(`${tabLabel}: must be an object.`)
      return
    }
    const tabRecord = tab as RawDashboard
    const label = typeof tabRecord.tab === 'string' ? tabRecord.tab.trim() : ''
    if (!label) {
      errors.push(`${tabLabel}: tab name is required.`)
      return
    }
    const tabParams = parseParamsObject(tabRecord.params, `${tabLabel} "${label}"`, errors)
    if (!tabParams) return
    const rawRows = Array.isArray(tabRecord.rows) ? tabRecord.rows : []
    if (rawRows.length === 0) {
      errors.push(`${tabLabel}: rows must be a non-empty array.`)
      return
    }

    const rawFilters = Array.isArray(tabRecord.filters) ? tabRecord.filters : []
    const filters: DashboardFilter[] = []
    rawFilters.forEach((filterEntry, filterIndex) => {
      const prefix = `${tabLabel} "${label}" filter ${filterIndex + 1}`
      if (!filterEntry || typeof filterEntry !== 'object') {
        errors.push(`${prefix}: must be an object.`)
        return
      }
      const filterRecord = filterEntry as RawDashboard
      const reference = typeof filterRecord.filter === 'string' ? filterRecord.filter.trim() : ''
      if (!reference) {
        errors.push(`${prefix}: filter reference is required.`)
        return
      }
      const match = ASSET_REF_PATTERN.exec(reference)
      if (!match) {
        errors.push(`${prefix}: filter must be asset://<uuid>.`)
        return
      }
      const rawType = typeof filterRecord.type === 'string' ? filterRecord.type.trim().toLowerCase() : ''
      const type: DashboardFilterType =
        rawType === '' || rawType === 'multi'
          ? 'multi'
          : rawType === 'single'
            ? 'single'
            : rawType === 'stepper'
              ? 'stepper'
              : 'multi'
      if (rawType && type !== rawType) {
        errors.push(`${prefix}: type must be "multi", "single", or "stepper".`)
        return
      }
      let defaultValue = ''
      const rawDefault = filterRecord.default
      if (typeof rawDefault === 'string') {
        defaultValue = rawDefault.trim()
      } else if (typeof rawDefault === 'number' && Number.isFinite(rawDefault)) {
        defaultValue = String(rawDefault)
      } else if (typeof rawDefault === 'boolean') {
        defaultValue = String(rawDefault)
      } else if (rawDefault !== undefined && rawDefault !== null) {
        errors.push(`${prefix}: default must be "first", "last", or a scalar value.`)
        return
      }
      filters.push({ reference, uuid: match[1], type, defaultValue })
    })

    const rows = parseRows(rawRows, `${tabLabel} "${label}"`, errors)
    if (rows.length === 0) {
      errors.push(`${tabLabel} "${label}": has no valid rows.`)
      return
    }
    tabs.push({ label, params: tabParams, rows, filters })
  })

  if (tabs.length === 0) {
    return { error: 'Dashboard YAML has no valid tabs.', details: errors }
  }
  if (errors.length > 0) {
    return { error: 'Dashboard YAML has validation errors.', details: errors }
  }

  return { tabs, errors }
}

const buildErrorHtml = (title: string, details?: string[]) => {
  const safeTitle = escapeHtml(title)
  const detailHtml = details?.length
    ? `<ul>${details.map((detail) => `<li>${escapeHtml(detail)}</li>`).join('')}</ul>`
    : ''

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        font-family: 'JetBrains Mono', Consolas, monospace;
        background-color: #ffffff;
        color: #111827;
        padding: 16px;
      }
      h2 {
        margin: 0 0 8px;
        font-size: 16px;
      }
      ul {
        margin: 8px 0 0;
        padding-left: 18px;
      }
      li {
        margin: 4px 0;
      }
    </style>
  </head>
  <body>
    <h2>${safeTitle}</h2>
    ${detailHtml}
  </body>
</html>`
}

const buildDashboardHtml = (parsed: ParsedDashboard, options?: RenderOptions) => {
  const dashboardGap = 8
  const hasTabs = parsed.tabs.length > 1
  const datasetDataApiScript = buildDatasetDataApiScript(insightsServiceUrl)
  const tabsHtml = hasTabs
    ? `<div class="dashboard-tabs" role="tablist">
        ${parsed.tabs
          .map(
            (tab, index) =>
              `<button class="dashboard-tab-button${index === 0 ? ' active' : ''}" role="tab" aria-selected="${
                index === 0 ? 'true' : 'false'
              }" data-tab-index="${index}" type="button">${escapeHtml(tab.label)}</button>`
          )
          .join('')}
      </div>`
    : ''

  const pagesHtml = parsed.tabs
    .map((tab, tabIndex) => {
      const rowsHtml = tab.rows
        .map((row) => {
          const resolvedWidths = resolveRowItemWidths(row.items)
          const itemCount = resolvedWidths.length
          const gapOffset = itemCount > 1 ? (dashboardGap * (itemCount - 1)) / itemCount : 0
          const widthStyle = (width: number) => `calc(${width}% - ${gapOffset}px)`
          const rowHeightPx = Number.parseFloat(row.height)
          const minWidthPercent = resolvedWidths.length > 0 ? Math.min(...resolvedWidths) : 0
          const itemsHtml = row.items
            .map(
              (item, index) => `
          <div class="dashboard-cell" data-asset-id="${escapeHtml(item.uuid)}" data-item-params="${escapeHtml(
            JSON.stringify(item.params)
          )}" style="flex: 0 0 ${widthStyle(resolvedWidths[index] ?? 0)}; width: ${widthStyle(
            resolvedWidths[index] ?? 0
          )};">
            <div class="dashboard-viz-root"></div>
          </div>`
            )
            .join('')
          return `<div class="dashboard-row" data-item-count="${itemCount}" data-min-width-fraction="${
            minWidthPercent / 100
          }" data-row-height-px="${Number.isFinite(rowHeightPx) ? rowHeightPx : 0}" style="--dashboard-row-height: ${
            row.height
          };">${itemsHtml}</div>`
        })
        .join('')
      const filtersHtml = tab.filters.length
        ? `<div class="dashboard-filters">
          ${tab.filters
            .map((filter) => {
              const filterName = ''
              if (filter.type === 'stepper') {
                return `<div class="dashboard-filter dashboard-filter-stepper" data-filter-id="${escapeHtml(
                  filter.uuid
                )}" data-tab-index="${tabIndex}" data-filter-type="stepper" data-filter-name="${escapeHtml(
                  filterName
                )}" data-filter-default="${escapeHtml(filter.defaultValue)}">
                  <button class="dashboard-stepper-button" type="button" data-direction="prev" aria-label="Previous value">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z"></path>
                    </svg>
                  </button>
                  <span class="dashboard-stepper-label"></span>
                  <button class="dashboard-stepper-button" type="button" data-direction="next" aria-label="Next value">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"></path>
                    </svg>
                  </button>
                </div>`
              }
              const isSingle = filter.type === 'single'
              const selectAllHtml = isSingle
                ? ''
                : `<label class="dashboard-filter-option" role="option">
                    <input type="checkbox" class="dashboard-filter-checkbox" data-select-all="true" />
                    <span class="dashboard-filter-option-label">Select all</span>
                  </label>
                  <div class="dashboard-filter-divider"></div>`
              return `<div class="dashboard-filter" data-filter-id="${escapeHtml(filter.uuid)}" data-tab-index="${tabIndex}" data-filter-type="${escapeHtml(
                filter.type
              )}" data-filter-default="${escapeHtml(filter.defaultValue)}">
                <button class="dashboard-filter-trigger" type="button" aria-expanded="false" data-filter-name="${escapeHtml(
                  filterName
                )}">
                  <span class="dashboard-filter-label"></span>
                  <span class="dashboard-filter-clear" aria-label="Clear filter" title="Clear filter">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                    </svg>
                  </span>
                  <svg class="dashboard-filter-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M7 10l5 5 5-5z"></path>
                  </svg>
                </button>
                <div class="dashboard-filter-menu" role="listbox">
                  ${selectAllHtml}
                  <div class="dashboard-filter-values"></div>
                </div>
              </div>`
            })
            .join('')}
        </div>`
        : ''
      return `<div class="dashboard-page${tabIndex === 0 ? ' active' : ''}" data-tab-index="${tabIndex}" data-tab-params="${escapeHtml(
        JSON.stringify(tab.params)
      )}">
        ${filtersHtml}
        ${rowsHtml}
      </div>`
    })
    .join('')

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      * {
        box-sizing: border-box;
      }
      body {
        margin: 0;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background-color: #ffffff;
        color: #0f172a;
        height: 100vh;
        overflow: hidden;
      }
      .dashboard {
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        --dashboard-gap: ${dashboardGap}px;
        --dashboard-border: #cbd5f5;
        --dashboard-surface: #f1f5f9;
      }
      .dashboard-tabs {
        display: flex;
        gap: var(--dashboard-gap);
        padding: 12px var(--dashboard-gap) 0;
        border-bottom: 1px solid var(--dashboard-border);
        background: #ffffff;
      }
      .dashboard-tab-button {
        border: 1px solid var(--dashboard-border);
        background: #ffffff;
        color: #0f172a;
        padding: 6px 12px;
        border-radius: 4px 4px 0 0;
        font-size: 13px;
        cursor: pointer;
        margin-bottom: -1px;
      }
      .dashboard-tab-button.active {
        background: var(--dashboard-surface);
        border-color: var(--dashboard-border);
        border-bottom-color: var(--dashboard-surface);
        color: #0f172a;
      }
      .dashboard-pages {
        flex: 1;
        min-height: 0;
        background: var(--dashboard-surface);
      }
      .dashboard-page {
        height: 100%;
        display: none;
        flex-direction: column;
        gap: var(--dashboard-gap);
        padding: var(--dashboard-gap);
        overflow-y: auto;
      }
      .dashboard-page.active {
        display: flex;
      }
      .dashboard-filters {
        display: flex;
        flex-wrap: wrap;
        gap: var(--dashboard-gap);
      }
      .dashboard-filter {
        position: relative;
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .dashboard-filter-stepper {
        flex-direction: row;
        align-items: center;
        gap: 8px;
        border: 1px solid var(--dashboard-border);
        border-radius: 4px;
        background: #ffffff;
        padding: 6px 8px;
      }
      .dashboard-stepper-label {
        min-width: 120px;
        text-align: center;
        font-size: 13px;
        white-space: nowrap;
      }
      .dashboard-stepper-button {
        border: none;
        background: transparent;
        color: #0f172a;
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        cursor: pointer;
      }
      .dashboard-stepper-button:disabled {
        color: #94a3b8;
        cursor: default;
      }
      .dashboard-stepper-button svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
      }
      .dashboard-filter-trigger {
        width: max-content;
        border: 1px solid var(--dashboard-border);
        border-radius: 4px;
        background: #ffffff;
        color: #0f172a;
        padding: 8px 10px;
        font-size: 13px;
        text-align: left;
        cursor: pointer;
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .dashboard-filter-label {
        white-space: nowrap;
      }
      .dashboard-filter-clear {
        display: none;
        width: 16px;
        height: 16px;
        align-items: center;
        justify-content: center;
        color: #64748b;
        cursor: pointer;
      }
      .dashboard-filter-trigger.has-selection .dashboard-filter-clear {
        display: inline-flex;
      }
      .dashboard-filter-clear svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
      }
      .dashboard-filter-icon {
        width: 18px;
        height: 18px;
        fill: currentColor;
        flex: 0 0 auto;
      }
      .dashboard-filter-menu {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        width: max-content;
        background: #ffffff;
        border: 1px solid var(--dashboard-border);
        border-radius: 4px;
        box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
        padding: 6px 0;
        display: none;
        z-index: 2;
        max-height: 200px;
        overflow-y: auto;
      }
      .dashboard-filter-menu.open {
        display: block;
      }
      .dashboard-filter-select {
        display: none;
      }
      .dashboard-filter-option {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        cursor: pointer;
        font-size: 13px;
        color: #0f172a;
      }
      .dashboard-filter-option:hover {
        background: #f8fafc;
      }
      .dashboard-filter[data-filter-type='single'] .dashboard-filter-option {
        gap: 0;
        margin: 0 6px;
        border-radius: 4px;
      }
      .dashboard-filter[data-filter-type='single'] .dashboard-filter-option.is-selected {
        background: #dbeafe;
        color: #1e3a8a;
      }
      .dashboard-filter[data-filter-type='single'] .dashboard-filter-option.is-selected:hover {
        background: #bfdbfe;
      }
      .dashboard-filter-option-label {
        flex: 1;
        white-space: nowrap;
      }
      .dashboard-filter-divider {
        height: 1px;
        background: #e2e8f0;
        margin: 4px 0;
      }
      .dashboard-filter-values {
        display: flex;
        flex-direction: column;
      }
      .dashboard-filter-checkbox {
        border: 1px solid var(--dashboard-border);
        accent-color: #1d4ed8;
      }
      .dashboard-filter[data-filter-type='single'] .dashboard-filter-checkbox {
        position: absolute;
        opacity: 0;
        pointer-events: none;
        width: 0;
        height: 0;
      }
      .dashboard-row {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: var(--dashboard-gap);
        flex: 0 0 auto;
        height: var(--dashboard-row-height);
        min-height: 0;
      }
      .dashboard-row.is-stacked {
        flex-direction: column;
        height: auto;
      }
      .dashboard-row:not(.is-stacked) .dashboard-cell {
        height: 100%;
      }
      .dashboard-row.is-stacked .dashboard-cell {
        width: 100% !important;
        flex: 0 0 auto !important;
        height: var(--dashboard-row-height);
      }
      .dashboard-cell {
        border: 1px dashed var(--dashboard-border);
        background: #ffffff;
        border-radius: 6px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        overflow: hidden;
      }
      .dashboard-viz-root {
        width: 100%;
        height: 100%;
      }
      .dashboard-asset-frame {
        border: none;
        width: 100%;
        height: 100%;
        display: block;
      }
    </style>
  </head>
  <body>
    <div class="dashboard">
      ${tabsHtml}
      <div class="dashboard-pages">
        ${pagesHtml}
      </div>
    </div>
    <script>
      const renderData = ${JSON.stringify(options?.data ?? null)};
      const auth = renderData && renderData.auth ? renderData.auth : null;
      const embedDepthRaw = renderData && renderData.context ? renderData.context.embedDepth : 0;
      const embedDepth = Number.isFinite(embedDepthRaw) && embedDepthRaw >= 0 ? embedDepthRaw : 0;
      const trellis = window.trellis;

      const logMessage = (message) => {
        trellis.log(message);
      };

      const escapeText = (value) =>
        String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

${datasetDataApiScript}
      const datasetDataApi = createDatasetDataApi({ auth, logMessage });

      const getPreloadedDatasetPayload = (datasetId) => {
        if (!trellis.assets || typeof trellis.assets.get !== 'function') return null;
        try {
          const handle = trellis.assets.get('asset://' + datasetId);
          if (!handle || typeof handle !== 'object') return null;
          if (
            Object.prototype.hasOwnProperty.call(handle, 'datasetPayload') &&
            handle.datasetPayload &&
            typeof handle.datasetPayload === 'object'
          ) {
            return handle.datasetPayload;
          }
          if (typeof handle.text === 'string' && handle.text.trim().length > 0) {
            return handle.text;
          }
        } catch {
          return null;
        }
        return null;
      };

      const resolveDatasetPayload = async (datasetId) => {
        const preloadedPayload = getPreloadedDatasetPayload(datasetId);
        if (preloadedPayload !== null) {
          return preloadedPayload;
        }
        if (!auth || !auth.accessToken || !auth.organizationId || !auth.entityApiUrl) {
          throw new Error('Missing authentication context');
        }
        return datasetDataApi.resolveDatasetPayload(datasetId);
      };

      const resolveColumns = (data) => {
        const fields = Array.isArray(data && data.fields) ? data.fields : [];
        const columnsFromFields = fields
          .map((field) => (field && field.name ? String(field.name).trim() : ''))
          .filter((name) => name.length > 0);
        if (columnsFromFields.length > 0) return columnsFromFields;
        const rows = Array.isArray(data && data.rows) ? data.rows : [];
        if (rows.length === 0 || !rows[0] || typeof rows[0] !== 'object') return [];
        return Object.keys(rows[0]);
      };

      const normalizeItemParamValue = (value) => {
        if (value === null || value === undefined) return null;
        if (Array.isArray(value)) {
          return value
            .filter((item) => item !== null && item !== undefined)
            .map((item) => String(item));
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return String(value);
        }
        return null;
      };

      const readItemParams = (cell, assetId) => {
        const rawParams = cell.getAttribute('data-item-params') || '';
        if (!rawParams) return {};
        try {
          const parsed = JSON.parse(rawParams);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
          const normalized = {};
          Object.entries(parsed).forEach(([rawKey, rawValue]) => {
            const key = String(rawKey || '').trim();
            if (!key) return;
            const normalizedValue = normalizeItemParamValue(rawValue);
            if (normalizedValue === null) return;
            normalized[key] = normalizedValue;
          });
          return normalized;
        } catch (error) {
          const message = error && error.message ? error.message : String(error);
          logMessage('Failed to parse dashboard item params for asset ' + assetId + ': ' + message);
          return {};
        }
      };

      const readTabParams = (pageNode, tabIndex) => {
        if (!pageNode) return {};
        const rawParams = pageNode.getAttribute('data-tab-params') || '';
        if (!rawParams) return {};
        try {
          const parsed = JSON.parse(rawParams);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
          const normalized = {};
          Object.entries(parsed).forEach(([rawKey, rawValue]) => {
            const key = String(rawKey || '').trim();
            if (!key) return;
            const normalizedValue = normalizeItemParamValue(rawValue);
            if (normalizedValue === null) return;
            normalized[key] = normalizedValue;
          });
          return normalized;
        } catch (error) {
          const message = error && error.message ? error.message : String(error);
          logMessage('Failed to parse dashboard tab params for tab ' + tabIndex + ': ' + message);
          return {};
        }
      };

      const mergeParams = (filterValues, tabParams, itemParams) => ({
        ...(filterValues || {}),
        ...(tabParams || {}),
        ...(itemParams || {}),
      });

      const loadDashboardItem = (cell) => {
        const assetId = cell.getAttribute('data-asset-id') || '';
        const root = cell.querySelector('.dashboard-viz-root');
        if (!root) return;
        if (!assetId) {
          logMessage('Dashboard asset reference missing.');
          return;
        }
        const itemParams = readItemParams(cell, assetId);
        root.innerHTML = '';
        const ensureInstanceId = () => {
          const existing = cell.getAttribute('data-instance-id');
          if (existing) return existing;
          const instanceId = window.crypto && typeof window.crypto.randomUUID === 'function'
            ? window.crypto.randomUUID()
            : 'asset-' + Date.now().toString(16) + '-' + Math.random().toString(16).slice(2);
          cell.setAttribute('data-instance-id', instanceId);
          return instanceId;
        };
        const instanceId = ensureInstanceId();
        readyEmbeddedAssetInstances.delete(instanceId);
        embeddedAssetWindows.delete(instanceId);
        const iframe = document.createElement('iframe');
        iframe.className = 'dashboard-asset-frame';
        iframe.setAttribute('title', 'Embedded asset');
        iframe.setAttribute('loading', 'lazy');
        iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts');
        const query = new URLSearchParams({
          depth: String(embedDepth + 1),
          instance: instanceId,
        });
        iframe.src = '/asset-embed/' + encodeURIComponent(assetId) + '?' + query.toString();
        iframe.addEventListener('load', () => {
          if (iframe.contentWindow) {
            embeddedAssetWindows.set(instanceId, iframe.contentWindow);
          }
          const pageNode = cell.closest('.dashboard-page');
          const tabIndex = pageNode ? Number(pageNode.getAttribute('data-tab-index') || '0') : 0;
          const tabParams = readTabParams(pageNode, tabIndex);
          updateEmbeddedAssetParams(
            instanceId,
            mergeParams(collectFilterValuesForTab(tabIndex), tabParams, itemParams)
          );
        });
        iframe.addEventListener('error', () => {
          logMessage('Unable to load embedded asset iframe for ' + assetId + '.');
          embeddedAssetWindows.delete(instanceId);
          readyEmbeddedAssetInstances.delete(instanceId);
          pendingParamsByInstance.delete(instanceId);
          revisionByInstance.delete(instanceId);
        });
        root.appendChild(iframe);
      };

      const buildOptionHtml = (value, inputType = 'checkbox', inputName = '') =>
        '<label class="dashboard-filter-option" role="option">' +
        '<input type="' +
        escapeText(inputType) +
        '" class="dashboard-filter-checkbox" data-value="' +
        escapeText(value) +
        '"' +
        (inputName ? ' name="' + escapeText(inputName) + '"' : '') +
        ' />' +
        '<span class="dashboard-filter-option-label">' +
        escapeText(value) +
        '</span>' +
        '</label>';

      const filterGroups = Array.from(document.querySelectorAll('.dashboard-filter'));
      const filtersByTab = new Map();

      filterGroups.forEach((filter) => {
        const tabIndex = Number(filter.getAttribute('data-tab-index') || '0');
        const list = filtersByTab.get(tabIndex) || [];
        list.push(filter);
        filtersByTab.set(tabIndex, list);
      });

      const loadedFilterTabs = new Set();
      const loadingFilterTabs = new Map();
      let refreshTabWithFilters = () => {};
      const pendingTabRefresh = new Map();
      const embeddedAssetWindows = new Map();
      const readyEmbeddedAssetInstances = new Set();
      const pendingParamsByInstance = new Map();
      const revisionByInstance = new Map();
      const filterControllers = new Map();

      const sendPendingParamsUpdate = (instanceId) => {
        if (!instanceId) return;
        if (!readyEmbeddedAssetInstances.has(instanceId)) return;
        const targetWindow = embeddedAssetWindows.get(instanceId);
        if (!targetWindow) return;
        if (!pendingParamsByInstance.has(instanceId)) return;
        const values = pendingParamsByInstance.get(instanceId) || {};
        const revision = revisionByInstance.get(instanceId) || 0;
        trellis.postMessage(targetWindow,
          {
            source: 'asset-params',
            type: 'update',
            target: String(instanceId),
            values,
            revision,
          },
        );
      };

      const updateEmbeddedAssetParams = (instanceId, values) => {
        if (!instanceId) return;
        pendingParamsByInstance.set(instanceId, values || {});
        revisionByInstance.set(instanceId, (revisionByInstance.get(instanceId) || 0) + 1);
        sendPendingParamsUpdate(instanceId);
      };

      const scheduleTabRefresh = (tabIndex) => {
        if (pendingTabRefresh.has(tabIndex)) return;
        const handle = setTimeout(() => {
          pendingTabRefresh.delete(tabIndex);
          refreshTabWithFilters(tabIndex);
        }, 0);
        pendingTabRefresh.set(tabIndex, handle);
      };

      const collectFilterValuesForTab = (tabIndex) => {
        const values = {};
        const filters = filtersByTab.get(tabIndex) || [];
        filters.forEach((filter) => {
          const controller = filterControllers.get(filter);
          if (!controller || typeof controller.getParamEntry !== 'function') return;
          const entry = controller.getParamEntry();
          if (!entry || !entry.key) return;
          values[entry.key] = entry.value;
        });
        return values;
      };

      const closeAllFilters = () => {
        filterGroups.forEach((filter) => {
          const trigger = filter.querySelector('.dashboard-filter-trigger');
          const menu = filter.querySelector('.dashboard-filter-menu');
          if (menu && trigger) {
            menu.classList.remove('open');
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
      };

      const createFilterController = (filter) => {
        const tabIndex = Number(filter.getAttribute('data-tab-index') || '0');
        const filterTypeRaw = String(filter.getAttribute('data-filter-type') || '')
          .trim()
          .toLowerCase();
        const filterType =
          filterTypeRaw === 'single' || filterTypeRaw === 'stepper' ? filterTypeRaw : 'multi';
        const filterDefault = String(filter.getAttribute('data-filter-default') || '').trim();
        const trigger = filter.querySelector('.dashboard-filter-trigger');
        const label = trigger ? trigger.querySelector('.dashboard-filter-label') : null;
        const clearControl = trigger ? trigger.querySelector('.dashboard-filter-clear') : null;
        const menu = filter.querySelector('.dashboard-filter-menu');
        const selectAllInput = filter.querySelector('input[data-select-all="true"]');
        const valuesContainer = filter.querySelector('.dashboard-filter-values');
        const filterId = String(filter.getAttribute('data-filter-id') || '');
        const singleGroupName = filterId
          ? 'dashboard-filter-single-' + filterId + '-' + tabIndex
          : 'dashboard-filter-single-' + tabIndex;
        const stepperLabel = filter.querySelector('.dashboard-stepper-label');
        const stepperPrev = filter.querySelector('.dashboard-stepper-button[data-direction="prev"]');
        const stepperNext = filter.querySelector('.dashboard-stepper-button[data-direction="next"]');
        const initialName = trigger?.getAttribute('data-filter-name') || filter.getAttribute('data-filter-name');
        let filterName = initialName ?? '';
        let stepperValues = [];
        let stepperIndex = -1;
        const closeMenu = () => {
          if (!menu || !trigger) return;
          menu.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');
        };

        const getValueInputs = () => Array.from(filter.querySelectorAll('input[data-value]'));
        const getSelectedValues = () =>
          getValueInputs()
            .filter((input) => input.checked)
            .map((input) => input.dataset.value || '')
            .filter((value) => value.length > 0);
        const resolveDefaultIndex = (
          values,
          fallbackToFirstWhenMissing = false,
          fallbackToFirstWhenInvalid = false
        ) => {
          if (!Array.isArray(values) || values.length === 0) {
            return -1;
          }
          if (filterDefault === 'first') {
            return 0;
          }
          if (filterDefault === 'last') {
            return values.length - 1;
          }
          if (filterDefault.length > 0) {
            const matchedIndex = values.findIndex((value) => value === filterDefault);
            if (matchedIndex >= 0) {
              return matchedIndex;
            }
            return fallbackToFirstWhenInvalid ? 0 : -1;
          }
          return fallbackToFirstWhenMissing ? 0 : -1;
        };
        const updateSingleOptionStyles = () => {
          if (filterType !== 'single') return;
          getValueInputs().forEach((input) => {
            const option = input.closest('.dashboard-filter-option');
            if (option) {
              option.classList.toggle('is-selected', Boolean(input.checked));
            }
          });
        };

        const updateSelectAllState = () => {
          if (filterType !== 'multi') return;
          if (!selectAllInput) return;
          const valueInputs = getValueInputs();
          const selectedCount = valueInputs.filter((input) => input.checked).length;
          const totalCount = valueInputs.length;
          if (selectedCount === 0) {
            selectAllInput.checked = false;
            selectAllInput.indeterminate = false;
          } else if (selectedCount === totalCount) {
            selectAllInput.checked = true;
            selectAllInput.indeterminate = false;
          } else {
            selectAllInput.checked = false;
            selectAllInput.indeterminate = true;
          }
        };

        const updateMenuLabel = () => {
          const selectedValues = getSelectedValues();
          if (trigger) {
            trigger.classList.toggle('has-selection', selectedValues.length > 0);
          }
          if (!label) return;
          if (!filterName) {
            label.textContent = '';
            return;
          }
          if (selectedValues.length === 0) {
            label.textContent = filterName;
            return;
          }
          if (selectedValues.length === 1) {
            label.textContent = filterName + ': ' + selectedValues[0];
            return;
          }
          label.textContent = filterName + ': ' + selectedValues.length + ' selections';
        };

        const updateStepperLabel = () => {
          if (!stepperLabel) return;
          const currentValue =
            stepperIndex >= 0 && stepperIndex < stepperValues.length ? stepperValues[stepperIndex] : '';
          filter.setAttribute('data-current-value', currentValue);
          if (!filterName) {
            stepperLabel.textContent = currentValue;
          } else if (!currentValue) {
            stepperLabel.textContent = filterName;
          } else {
            stepperLabel.textContent = filterName + ': ' + currentValue;
          }
          if (stepperPrev) {
            stepperPrev.disabled = stepperIndex <= 0;
          }
          if (stepperNext) {
            stepperNext.disabled = stepperIndex < 0 || stepperIndex >= stepperValues.length - 1;
          }
        };

        const refreshMenuState = (refreshTab = false) => {
          updateSelectAllState();
          updateMenuLabel();
          updateSingleOptionStyles();
          if (refreshTab) {
            scheduleTabRefresh(tabIndex);
          }
        };

        const setAllValuesChecked = (checked) => {
          const valueInputs = getValueInputs();
          valueInputs.forEach((input) => {
            input.checked = checked;
          });
          refreshMenuState(true);
        };

        const bindValueInputs = () => {
          const valueInputs = getValueInputs();
          valueInputs.forEach((input) => {
            if (filterType === 'single') {
              input.addEventListener('click', () => {
                closeMenu();
              });
            }
            input.addEventListener('change', () => {
              refreshMenuState(true);
              if (filterType === 'single' && input.checked) {
                closeMenu();
              }
            });
          });
          refreshMenuState(false);
        };

        if (trigger && menu && filterType !== 'stepper') {
          trigger.addEventListener('click', () => {
            const isOpen = menu.classList.contains('open');
            closeAllFilters();
            if (!isOpen) {
              menu.classList.add('open');
              trigger.setAttribute('aria-expanded', 'true');
            }
          });
        }

        if (clearControl) {
          clearControl.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            setAllValuesChecked(false);
          });
        }

        if (selectAllInput && filterType === 'multi') {
          selectAllInput.addEventListener('change', () => {
            const valueInputs = getValueInputs();
            const selectedCount = valueInputs.filter((input) => input.checked).length;
            const totalCount = valueInputs.length;
            const shouldSelectAll = totalCount > 0 && selectedCount < totalCount;
            setAllValuesChecked(shouldSelectAll);
          });
        }

        if (filterType === 'stepper') {
          if (stepperPrev) {
            stepperPrev.addEventListener('click', () => {
              if (stepperIndex <= 0) return;
              stepperIndex -= 1;
              updateStepperLabel();
              scheduleTabRefresh(tabIndex);
            });
          }
          if (stepperNext) {
            stepperNext.addEventListener('click', () => {
              if (stepperIndex < 0 || stepperIndex >= stepperValues.length - 1) return;
              stepperIndex += 1;
              updateStepperLabel();
              scheduleTabRefresh(tabIndex);
            });
          }
        }

        refreshMenuState(false);
        updateStepperLabel();

        return {
          setFilterName: (name) => {
            filterName = name;
            if (trigger) {
              trigger.setAttribute('data-filter-name', name);
            } else {
              filter.setAttribute('data-filter-name', name);
            }
            updateMenuLabel();
            updateStepperLabel();
          },
          setOptions: (values) => {
            if (filterType === 'stepper') {
              stepperValues = Array.isArray(values) ? [...values] : [];
              stepperIndex = resolveDefaultIndex(stepperValues, true, true);
              updateStepperLabel();
              if (stepperIndex >= 0) {
                scheduleTabRefresh(tabIndex);
              }
              return;
            }
            if (!valuesContainer) return;
            const inputType = filterType === 'single' ? 'radio' : 'checkbox';
            const inputName = filterType === 'single' ? singleGroupName : '';
            valuesContainer.innerHTML = values
              .map((value) => buildOptionHtml(value, inputType, inputName))
              .join('');
            const defaultIndex = resolveDefaultIndex(values);
            if (defaultIndex >= 0) {
              const valueInputs = getValueInputs();
              const defaultInput = valueInputs[defaultIndex];
              if (defaultInput) {
                defaultInput.checked = true;
              }
            }
            bindValueInputs();
            if (defaultIndex >= 0) {
              scheduleTabRefresh(tabIndex);
            }
          },
          getParamEntry: () => {
            const keySource = trigger?.getAttribute('data-filter-name') || filter.getAttribute('data-filter-name');
            const key = keySource ? keySource.trim() : '';
            if (!key) return null;
            if (filterType === 'stepper') {
              if (stepperIndex < 0 || stepperIndex >= stepperValues.length) return null;
              return { key, value: stepperValues[stepperIndex] };
            }
            const selectedValues = getSelectedValues();
            if (selectedValues.length === 0) return null;
            if (filterType === 'single') {
              return { key, value: selectedValues[0] };
            }
            return { key, value: selectedValues };
          },
        };
      };
      filterGroups.forEach((filter) => {
        filterControllers.set(filter, createFilterController(filter));
      });

      const removeFilter = (filter) => {
        const tabIndex = Number(filter.getAttribute('data-tab-index') || '0');
        const filters = filtersByTab.get(tabIndex);
        if (Array.isArray(filters)) {
          filtersByTab.set(
            tabIndex,
            filters.filter((entry) => entry !== filter)
          );
        }
        filterControllers.delete(filter);
        const container = filter.closest('.dashboard-filters');
        filter.remove();
        if (container && container.querySelectorAll('.dashboard-filter').length === 0) {
          container.remove();
        }
      };

      const loadFilterData = async (filter) => {
        const filterId = filter.getAttribute('data-filter-id');
        if (!filterId) {
          logMessage('Dashboard filter missing dataset reference.');
          removeFilter(filter);
          return;
        }

        const controller = filterControllers.get(filter);

        try {
          const datasetPayload = await resolveDatasetPayload(filterId);
          const data = await datasetDataApi.loadDatasetRows({ payload: datasetPayload });
          const rows = Array.isArray(data && data.rows) ? data.rows : [];
          const columns = resolveColumns(data);
          if (columns.length !== 1) {
            throw new Error('Dataset must return exactly one column.');
          }
          const columnName = columns[0];
          if (rows.length === 0) {
            logMessage('Filter ' + filterId + ' returned no rows.');
            removeFilter(filter);
            return;
          }
          const values = rows.map((row) => {
            if (!row || typeof row !== 'object') return '';
            return String(row[columnName] ?? '');
          });
          controller?.setFilterName(columnName);
          controller?.setOptions(values);
        } catch (error) {
          const message = error && error.message ? error.message : String(error);
          logMessage('Filter ' + filterId + ' failed: ' + message);
          removeFilter(filter);
        }
      };

      const loadFiltersForTab = async (tabIndex) => {
        if (loadedFilterTabs.has(tabIndex)) return;
        const inFlight = loadingFilterTabs.get(tabIndex);
        if (inFlight) {
          await inFlight;
          return;
        }
        const loadPromise = (async () => {
          const filters = filtersByTab.get(tabIndex) || [];
          await Promise.all(filters.map((filter) => loadFilterData(filter)));
        })()
          .finally(() => {
            loadingFilterTabs.delete(tabIndex);
            loadedFilterTabs.add(tabIndex);
          });
        loadingFilterTabs.set(tabIndex, loadPromise);
        await loadPromise;
      };

      const buttons = Array.from(document.querySelectorAll('.dashboard-tab-button'));
      const pages = Array.from(document.querySelectorAll('.dashboard-page'));
      const dashboardRows = Array.from(document.querySelectorAll('.dashboard-row'));
      const loadedAssetTabs = new Set();
      let activationToken = 0;

      const readRowGapPx = (row) => {
        const styles = window.getComputedStyle(row);
        const gapSource = styles.columnGap && styles.columnGap !== 'normal' ? styles.columnGap : styles.gap;
        const gap = Number.parseFloat(gapSource || '0');
        return Number.isFinite(gap) ? gap : 0;
      };

      const updateRowLayoutMode = (row) => {
        const itemCount = Number(row.getAttribute('data-item-count') || '0');
        const minWidthFraction = Number(row.getAttribute('data-min-width-fraction') || '0');
        const rowHeightPx = Number(row.getAttribute('data-row-height-px') || '0');
        if (
          !Number.isFinite(itemCount) ||
          itemCount <= 1 ||
          !Number.isFinite(minWidthFraction) ||
          minWidthFraction <= 0 ||
          !Number.isFinite(rowHeightPx) ||
          rowHeightPx <= 0
        ) {
          row.classList.remove('is-stacked');
          return;
        }

        const gapPx = readRowGapPx(row);
        const gapOffsetPx = itemCount > 1 ? (gapPx * (itemCount - 1)) / itemCount : 0;
        const smallestItemWidthPx = row.clientWidth * minWidthFraction - gapOffsetPx;
        row.classList.toggle('is-stacked', smallestItemWidthPx < rowHeightPx);
      };

      const updateAllRowLayouts = () => {
        dashboardRows.forEach((row) => {
          updateRowLayoutMode(row);
        });
      };

      const rowResizeObserver =
        typeof ResizeObserver === 'function'
          ? new ResizeObserver((entries) => {
              entries.forEach((entry) => {
                if (entry && entry.target instanceof HTMLElement) {
                  updateRowLayoutMode(entry.target);
                }
              });
            })
          : null;

      if (rowResizeObserver) {
        dashboardRows.forEach((row) => {
          rowResizeObserver.observe(row);
        });
      } else {
        window.addEventListener('resize', updateAllRowLayouts);
      }
      updateAllRowLayouts();

      window.addEventListener('message', (event) => {
        const data = event && event.data;
        if (!data || typeof data !== 'object') return;
        if (data.source === 'asset-params' && data.type === 'ready' && typeof data.instanceId === 'string') {
          readyEmbeddedAssetInstances.add(data.instanceId);
          sendPendingParamsUpdate(data.instanceId);
          return;
        }
        if (data.source !== 'asset-renderer') return;
        if (event.source === window || event.source === window.parent) return;
        if (data.type !== 'log') return;
        trellis.emit(data, window.parent);
      });

      refreshTabWithFilters = (tabIndex) => {
        if (!loadedAssetTabs.has(tabIndex)) return;
        const filterValues = collectFilterValuesForTab(tabIndex);
        const page = pages.find((node) => Number(node.getAttribute('data-tab-index') || '0') === tabIndex);
        const tabParams = readTabParams(page, tabIndex);
        const cells = page ? Array.from(page.querySelectorAll('.dashboard-cell')) : [];
        cells.forEach((cell) => {
          const instanceId = cell.getAttribute('data-instance-id');
          const assetId = cell.getAttribute('data-asset-id') || '';
          const itemParams = readItemParams(cell, assetId);
          if (instanceId) {
            updateEmbeddedAssetParams(instanceId, mergeParams(filterValues, tabParams, itemParams));
          }
        });
      };

      const loadAssetsForTab = (tabIndex) => {
        if (loadedAssetTabs.has(tabIndex)) {
          refreshTabWithFilters(tabIndex);
          return;
        }
        loadedAssetTabs.add(tabIndex);
        const page = pages.find((node) => Number(node.getAttribute('data-tab-index') || '0') === tabIndex);
        const cells = page ? Array.from(page.querySelectorAll('.dashboard-cell')) : [];
        cells.forEach((cell) => {
          loadDashboardItem(cell);
        });
      };

      const activateTab = async (index) => {
        const token = ++activationToken;
        if (buttons.length) {
          buttons.forEach((button, idx) => {
            const isActive = idx === index;
            button.classList.toggle('active', isActive);
            button.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
        }
        pages.forEach((page, idx) => {
          page.classList.toggle('active', idx === index);
        });
        updateAllRowLayouts();
        await loadFiltersForTab(index);
        if (token !== activationToken) return;
        loadAssetsForTab(index);
      };

      if (buttons.length) {
        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const index = Number(button.getAttribute('data-tab-index') || 0);
            void activateTab(Number.isFinite(index) ? index : 0);
          });
        });
        void activateTab(0);
      } else {
        void activateTab(0);
      }

      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('.dashboard-filter')) return;
        closeAllFilters();
      });
    </script>
  </body>
</html>`
}

const dashboardRenderer = (content: string, options?: RenderOptions) => {
  const parsed = parseDashboard(content || '')
  if ('error' in parsed) {
    return buildErrorHtml(parsed.error, parsed.details)
  }
  return buildDashboardHtml(parsed, options)
}

export default dashboardRenderer

export const collectAssetReferences = (content: string, renderData?: RenderData) => {
  const matches = new Set(collectAssetReferencesFromRenderData(content, renderData))
  let parsed: unknown
  try {
    parsed = YAML.parse(content || '')
  } catch {
    return Array.from(matches)
  }
  if (!parsed || typeof parsed !== 'object') return Array.from(matches)
  const record = parsed as RawDashboard
  const rawTabs = Array.isArray(record.tabs) ? record.tabs : []
  rawTabs.forEach((tab) => {
    if (!tab || typeof tab !== 'object') return
    const tabRecord = tab as RawDashboard
    const rawFilters = Array.isArray(tabRecord.filters) ? tabRecord.filters : []
    rawFilters.forEach((filterEntry) => {
      if (!filterEntry || typeof filterEntry !== 'object') return
      const filterRecord = filterEntry as RawDashboard
      const reference = typeof filterRecord.filter === 'string' ? filterRecord.filter.trim() : ''
      const match = ASSET_REF_PATTERN.exec(reference)
      if (match) matches.add(normalizeAssetId(match[1]))
    })
    const rawRows = Array.isArray(tabRecord.rows) ? tabRecord.rows : []
    rawRows.forEach((row) => {
      if (!row || typeof row !== 'object') return
      const rowRecord = row as RawDashboard
      const rawItems = Array.isArray(rowRecord.items) ? rowRecord.items : []
      rawItems.forEach((item) => {
        if (!item || typeof item !== 'object') return
        const itemRecord = item as RawDashboard
        const assetRef = typeof itemRecord.asset === 'string' ? itemRecord.asset.trim() : ''
        const match = ASSET_REF_PATTERN.exec(assetRef)
        if (match) matches.add(normalizeAssetId(match[1]))
      })
    })
  })
  return Array.from(matches)
}
